import React, { useEffect, useState, useRef, useContext } from 'react';
import { CookieConsentService } from '../../services/cookieConsent/CookieConsentService';
import { AdPerformanceTracker } from '../../services/analytics/AdPerformanceTracker';
import { AdRefreshManager } from '../../services/analytics/AdRefreshManager';
import { AuthContext } from '../Providers/AuthContext';
import styles from './AdSense.module.css';

interface AdSenseProps {
  className?: string;
  enableRefresh?: boolean;
  slotId?: string;
}

// AdSense Configuration
// IMPORTANT: Replace this with your actual ad slot ID from AdSense dashboard
// Go to: AdSense > Ads > By ad unit > Create new ad unit > Get the slot ID
const ADSENSE_SLOT_ID = '1234567890'; // TODO: Replace with real slot ID from AdSense

/**
 * Google AdSense component for displaying ads
 * Only loads ads if user has consented to advertising cookies
 * Includes performance tracking and optional ad refresh
 */
function AdSense({ className, enableRefresh = false, slotId }: AdSenseProps) {
  return null; // TEMP: disable ads completely
  const [hasConsent, setHasConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshCount, setRefreshCount] = useState(0);
  const adContainerRef = useRef<HTMLDivElement>(null);
  const adSlotId = useRef(slotId || `ad-sidebar-${Math.random().toString(36).substr(2, 9)}`);
  const { isPremium } = useContext(AuthContext);

  useEffect(() => {
    // Check for advertising consent
    const checkConsent = () => {
      const advertisingConsent = CookieConsentService.hasAdvertisingConsent();
      setHasConsent(advertisingConsent);
      setIsLoading(false);
    };

    checkConsent();

    // Listen for consent changes
    const cleanup = CookieConsentService.onConsentChange(() => {
      checkConsent();
    });

    return cleanup;
  }, []);

  useEffect(() => {
    // Only load ads if user has consented and in production
    if (!hasConsent || process.env.NODE_ENV !== 'production') {
      return;
    }

    try {
      // Push ad to AdSense
      if (window.adsbygoogle) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }

      // Track ad impression
      AdPerformanceTracker.trackAdImpression({
        adSlot: adSlotId.current,
        adFormat: 'sidebar',
        pageUrl: window.location.pathname,
        timestamp: Date.now(),
        isVisible: true,
      });

      // Setup viewability tracking
      if (adContainerRef.current) {
        AdPerformanceTracker.trackAdViewability(
          adContainerRef.current,
          adSlotId.current,
          'sidebar'
        );
      }

      // Setup ad refresh if enabled
      if (enableRefresh && adContainerRef.current) {
        AdRefreshManager.startRefresh(
          adContainerRef.current,
          adSlotId.current,
          handleAdRefresh
        );
      }
    } catch (error) {
    }

    // Cleanup on unmount
    return () => {
      AdPerformanceTracker.stopViewabilityTracking(adSlotId.current);
      if (enableRefresh) {
        AdRefreshManager.stopRefresh(adSlotId.current);
      }
    };
  }, [hasConsent, refreshCount, enableRefresh]);

  // Track user ad properties
  useEffect(() => {
    AdPerformanceTracker.setUserAdProperties({
      hasAdConsent: hasConsent,
      isPremium: isPremium || false,
    });
  }, [hasConsent, isPremium]);

  // Handle ad refresh
  const handleAdRefresh = () => {
    try {
      // Increment refresh count to trigger re-render
      setRefreshCount((prev) => prev + 1);

    } catch (error) {
    }
  };

  // Don't show ads if no consent
  if (!hasConsent) {
    if (isLoading) {
      // Show loading state while checking consent
      return <div className={`${styles.adContainer} ${styles.adLoading} ${className || ''}`} />;
    }
    // No consent - don't show ad space
    return null;
  }

  return (
    <div
      ref={adContainerRef}
      className={`${styles.adContainer} ${className || ''}`}
      data-ad-slot-id={adSlotId.current}
    >
      {/* Google AdSense Display Ad Unit */}
      <ins
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client="ca-pub-4242622741679918"
        data-ad-slot={ADSENSE_SLOT_ID}
        data-ad-format="auto"
        data-full-width-responsive="true"
        key={`ad-${adSlotId.current}-${refreshCount}`}
      />
    </div>
  );
}

export default AdSense;

// Extend Window interface for TypeScript
declare global {
  interface Window {
    adsbygoogle: any[];
  }
}
